import { mulaw } from 'alawmulaw';

export const WEB_CALL_SAMPLE_RATE = 8000;

export const float32ToInt16 = (samples) => {
    const int16 = new Int16Array(samples.length);
    for (let i = 0; i < samples.length; i++) {
        const sample = Math.max(-1, Math.min(1, samples[i]));
        int16[i] = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
    }
    return int16;
};

export const downsampleFloat32 = (samples, inputSampleRate, outputSampleRate = WEB_CALL_SAMPLE_RATE) => {
    if (!inputSampleRate || inputSampleRate === outputSampleRate) {
        return samples;
    }

    const ratio = inputSampleRate / outputSampleRate;
    const length = Math.max(1, Math.round(samples.length / ratio));
    const result = new Float32Array(length);

    for (let i = 0; i < length; i++) {
        const start = Math.floor(i * ratio);
        const end = Math.min(Math.floor((i + 1) * ratio), samples.length);
        let sum = 0;
        let count = 0;

        for (let j = start; j < end; j++) {
            sum += samples[j];
            count++;
        }

        result[i] = count > 0 ? sum / count : samples[start] || 0;
    }

    return result;
};

export const uint8ArrayToBase64 = (bytes) => {
    let binary = '';
    const chunkSize = 0x8000;

    for (let i = 0; i < bytes.length; i += chunkSize) {
        binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }

    return btoa(binary);
};

export const base64ToUint8Array = (base64) => {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }

    return bytes;
};

export const calculateRms = (samples) => {
    if (!samples?.length) return 0;
    let sum = 0;

    for (let i = 0; i < samples.length; i++) {
        sum += samples[i] * samples[i];
    }

    return Math.sqrt(sum / samples.length);
};

export const createMediaMessage = (samples, inputSampleRate, timestampMs) => {
    const downsampled = downsampleFloat32(samples, inputSampleRate);
    const pcm = float32ToInt16(downsampled);
    const encoded = mulaw.encode(pcm);
    const timestamp = Math.round(timestampMs);

    return {
        message: {
            event: 'media',
            media: {
                payload: uint8ArrayToBase64(encoded),
                timestamp
            }
        },
        durationMs: (downsampled.length / WEB_CALL_SAMPLE_RATE) * 1000
    };
};

export const decodeMediaPayload = (payload) => {
    const encoded = base64ToUint8Array(payload);
    const pcm = mulaw.decode(encoded);
    return Float32Array.from(pcm, sample => sample / 32768);
};

export const createPlaybackQueue = (audioContext, {
    onBotSpeakingChange = () => {}
} = {}) => {
    const queue = [];
    let currentSource = null;
    let generation = 0;

    const playNext = () => {
        if (currentSource || queue.length === 0) {
            if (!currentSource && queue.length === 0) {
                onBotSpeakingChange(false);
            }
            return;
        }

        const samples = queue.shift();
        const playGeneration = generation;
        const buffer = audioContext.createBuffer(1, samples.length, WEB_CALL_SAMPLE_RATE);
        buffer.copyToChannel(samples, 0);

        const source = audioContext.createBufferSource();
        currentSource = source;
        source.buffer = buffer;
        source.connect(audioContext.destination);
        source.onended = () => {
            if (playGeneration !== generation) return;
            currentSource = null;
            playNext();
        };

        onBotSpeakingChange(true);
        source.start();
    };

    const enqueue = (samples) => {
        if (!samples?.length) return;
        queue.push(samples);
        playNext();
    };

    const clear = () => {
        generation++;
        queue.length = 0;

        if (currentSource) {
            const source = currentSource;
            currentSource = null;
            source.onended = null;
            try {
                source.stop();
            } catch {
                // Source may already have ended.
            }
        }

        onBotSpeakingChange(false);
    };

    return {
        enqueue,
        clear,
        getQueueLength: () => queue.length,
        getCurrentSource: () => currentSource
    };
};

export class NativeWebCallClient {
    constructor({
        wsUrl,
        audioWorkletUrl = '/processor.js',
        WebSocketImpl = window.WebSocket,
        AudioContextImpl = window.AudioContext || window.webkitAudioContext,
        AudioWorkletNodeImpl = window.AudioWorkletNode,
        mediaDevices = navigator.mediaDevices,
        onStateChange = () => {},
        onUserSpeakingChange = () => {},
        onBotSpeakingChange = () => {},
        onError = () => {}
    }) {
        this.wsUrl = wsUrl;
        this.audioWorkletUrl = audioWorkletUrl;
        this.WebSocketImpl = WebSocketImpl;
        this.AudioContextImpl = AudioContextImpl;
        this.AudioWorkletNodeImpl = AudioWorkletNodeImpl;
        this.mediaDevices = mediaDevices;
        this.onStateChange = onStateChange;
        this.onUserSpeakingChange = onUserSpeakingChange;
        this.onBotSpeakingChange = onBotSpeakingChange;
        this.onError = onError;

        this.ws = null;
        this.audioContext = null;
        this.micStream = null;
        this.micSource = null;
        this.workletNode = null;
        this.silentGain = null;
        this.playback = null;
        this.mediaTimestampMs = 0;
        this.streamSid = 'WebcallStream';
        this.callSid = 'webcall';
        this.micEnabled = true;
        this.state = 'idle';
    }

    get isMicEnabled() {
        return this.micEnabled;
    }

    setState(state) {
        this.state = state;
        this.onStateChange(state);
    }

    async connect({ token, callSid = 'webcall', streamSid = 'WebcallStream' }) {
        this.callSid = callSid;
        this.streamSid = streamSid;
        this.setState('connecting');

        try {
            await this.initializeAudio();
            await this.connectSocket(token);
        } catch (error) {
            this.onError(error);
            await this.disconnect({ notify: false });
            this.setState('idle');
            throw error;
        }
    }

    async initializeAudio() {
        this.audioContext = new this.AudioContextImpl({ sampleRate: WEB_CALL_SAMPLE_RATE });

        if (this.audioContext.state === 'suspended') {
            await this.audioContext.resume();
        }

        this.playback = createPlaybackQueue(this.audioContext, {
            onBotSpeakingChange: this.onBotSpeakingChange
        });

        this.micStream = await this.mediaDevices.getUserMedia({ audio: true });
        await this.audioContext.audioWorklet.addModule(this.audioWorkletUrl);

        this.micSource = this.audioContext.createMediaStreamSource(this.micStream);
        this.workletNode = new this.AudioWorkletNodeImpl(this.audioContext, 'mic-processor');
        this.workletNode.port.onmessage = (event) => this.handleMicFrame(event.data);

        this.silentGain = this.audioContext.createGain();
        this.silentGain.gain.value = 0;

        this.micSource.connect(this.workletNode);
        this.workletNode.connect(this.silentGain);
        this.silentGain.connect(this.audioContext.destination);
    }

    connectSocket(token) {
        return new Promise((resolve, reject) => {
            this.ws = new this.WebSocketImpl(this.wsUrl);
            let settled = false;

            this.ws.onopen = () => {
                settled = true;
                this.sendJson({
                    event: 'start',
                    streamSid: this.streamSid,
                    start: {
                        accountSid: '',
                        callSid: this.callSid,
                        customParameters: {
                            jwt: token
                        }
                    }
                });
                this.setState('connected');
                resolve();
            };

            this.ws.onmessage = (message) => this.handleSocketMessage(message);
            this.ws.onerror = (event) => {
                const error = new Error('Web call socket error');
                this.onError(error);
                if (!settled) {
                    settled = true;
                    reject(error);
                }
            };
            this.ws.onclose = () => {
                this.cleanupAudio();
                this.setState('idle');
            };
        });
    }

    handleMicFrame(samples) {
        if (!this.micEnabled || this.ws?.readyState !== this.WebSocketImpl.OPEN) return;

        const floatSamples = samples instanceof Float32Array ? samples : new Float32Array(samples);
        this.onUserSpeakingChange(calculateRms(floatSamples) > 0.03);

        const { message, durationMs } = createMediaMessage(
            floatSamples,
            this.audioContext?.sampleRate || WEB_CALL_SAMPLE_RATE,
            this.mediaTimestampMs
        );
        this.mediaTimestampMs += durationMs;
        this.sendJson(message);
    }

    handleSocketMessage(message) {
        try {
            const data = typeof message.data === 'string'
                ? JSON.parse(message.data)
                : JSON.parse(new TextDecoder().decode(message.data));

            if (data.event === 'media' && data.media?.payload) {
                this.playback?.enqueue(decodeMediaPayload(data.media.payload));
            } else if (data.event === 'clear') {
                this.playback?.clear();
            } else if (data.event === 'stop') {
                this.disconnect();
            }
        } catch (error) {
            this.onError(error);
        }
    }

    sendJson(message) {
        if (this.ws?.readyState === this.WebSocketImpl.OPEN) {
            this.ws.send(JSON.stringify(message));
        }
    }

    setMicEnabled(enabled) {
        this.micEnabled = enabled;
        this.micStream?.getAudioTracks().forEach(track => {
            track.enabled = enabled;
        });
        this.onUserSpeakingChange(false);
    }

    async disconnect({ notify = true } = {}) {
        if (notify) {
            this.sendJson({
                event: 'stop',
                streamSid: this.streamSid
            });
        }

        try {
            this.ws?.close();
        } catch {
            // Socket may already be closed.
        }

        this.cleanupAudio();
        this.setState('idle');
    }

    cleanupAudio() {
        this.playback?.clear();
        this.playback = null;

        try { this.workletNode?.disconnect(); } catch {}
        try { this.silentGain?.disconnect(); } catch {}
        try { this.micSource?.disconnect(); } catch {}

        this.micStream?.getTracks().forEach(track => track.stop());

        if (this.audioContext && this.audioContext.state !== 'closed') {
            this.audioContext.close()?.catch?.(() => {});
        }

        this.ws = null;
        this.audioContext = null;
        this.micStream = null;
        this.micSource = null;
        this.workletNode = null;
        this.silentGain = null;
        this.onUserSpeakingChange(false);
        this.onBotSpeakingChange(false);
    }
}
